import { useRef, useState } from "react";
import {
  AnimatePresence,
  motion,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
} from "motion/react";
import ServiceVisual from "../services/service-visual";
import {
  categoryStartIndexes,
  scrollToStoryStep,
  serviceTimeline,
} from "@/config/service-timeline";
import { useHardwareAcceleration } from "@/hooks/use-hardware-acceleration";
import LiquidGlass from "../ui/liquid-glass";
import ServiceProgress from "../ui/service-progress";
import RevealText, { cinematicEase } from "../ui/reveal-text";

const MOBILE_SCROLL_DISTANCE_PER_SLIDE = 130;

export default function MobileServices() {
  const storyRef = useRef<HTMLElement>(null);
  const [activeStep, setActiveStep] = useState(0);
  const reduceMotion = useReducedMotion();
  const hasHardwareAcceleration = useHardwareAcceleration();
  const motionDisabled = Boolean(reduceMotion) || !hasHardwareAcceleration;
  const { scrollYProgress } = useScroll({
    target: storyRef,
    offset: ["start start", "end end"],
  });

  useMotionValueEvent(scrollYProgress, "change", (progress) => {
    const nextStep = Math.min(
      serviceTimeline.length - 1,
      Math.floor(
        Math.min(0.999, Math.max(0, progress)) * serviceTimeline.length
      )
    );

    setActiveStep((current) => (current === nextStep ? current : nextStep));
  });

  const currentStep = serviceTimeline[activeStep]!;
  const { category, categoryIndex, slide, slideIndex } = currentStep;
  const slideKey = `${categoryIndex}-${slideIndex}`;
  const storyHeight = Math.max(
    560,
    serviceTimeline.length * MOBILE_SCROLL_DISTANCE_PER_SLIDE
  );

  const jumpToCategory = (index: number) => {
    if (!storyRef.current) return;
    scrollToStoryStep(
      storyRef.current,
      categoryStartIndexes[index]!,
      serviceTimeline.length
    );
  };

  return (
    <section
      id="services"
      ref={storyRef}
      data-mobile-section="services"
      className="relative scroll-mt-18 px-4"
      style={{ minHeight: `${storyHeight}svh` }}
    >
      <div className="sticky top-18 h-[calc(100svh-4.5rem)] touch-pan-y transform-gpu pb-4 pt-8 [backface-visibility:hidden]">
        <div className="flex h-full min-h-0 flex-col">
          <div className="mb-4 flex shrink-0 items-end gap-3">
            <div className="relative h-12 w-[4.6rem] shrink-0 overflow-hidden">
              <AnimatePresence mode="popLayout" initial={false}>
                <motion.span
                  key={categoryIndex}
                  initial={{ y: motionDisabled ? 0 : "100%", opacity: 0 }}
                  animate={{ y: "0%", opacity: 1 }}
                  exit={{ y: motionDisabled ? 0 : "-100%", opacity: 0 }}
                  transition={{
                    duration: motionDisabled ? 0.35 : 0.6,
                    ease: cinematicEase,
                  }}
                  className="text-outline absolute inset-0 text-5xl font-medium leading-none tracking-[-0.04em]"
                  aria-hidden="true"
                >
                  {String(categoryIndex + 1).padStart(2, "0")}
                </motion.span>
              </AnimatePresence>
            </div>
            <div className="relative h-12 min-w-0 flex-1">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={category.title}
                  exit={{ opacity: 0, y: motionDisabled ? 0 : -8 }}
                  transition={{ duration: motionDisabled ? 0.15 : 0.2 }}
                  className="absolute inset-0 flex items-end"
                >
                  <RevealText
                    as="h2"
                    text={category.title}
                    stagger={0.035}
                    duration={0.6}
                    className="text-[1.05rem] font-normal leading-tight"
                  />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          <LiquidGlass className="min-h-0 w-full flex-1 bg-white/[0.06]">
            <div className="h-full p-2.5">
              <div className="relative h-full min-h-0 w-full select-none overflow-hidden rounded-[16px] bg-[#071019]/80">
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.035)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.035)_1px,transparent_1px)] bg-[size:36px_36px] [mask-image:radial-gradient(ellipse_at_50%_70%,black,transparent_75%)]"
                />
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -bottom-16 left-1/2 h-3/4 w-[120%] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(20,156,255,0.22),transparent)] blur-2xl"
                />
                <div
                  className="absolute inset-0 flex flex-col items-center justify-around gap-2 p-4"
                  aria-live="polite"
                >
                  <div className="relative z-10 min-h-[5.5rem] w-full max-w-[310px] text-center">
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.div
                        key={slideKey}
                        exit={{ opacity: 0, y: motionDisabled ? 0 : -10 }}
                        transition={{ duration: motionDisabled ? 0.15 : 0.2 }}
                      >
                        <RevealText
                          as="p"
                          text={slide.title}
                          stagger={0.02}
                          duration={0.6}
                          className="text-lg font-light leading-snug"
                        />
                      </motion.div>
                    </AnimatePresence>
                  </div>
                  <div className="relative z-10 flex h-[55%] w-full max-w-[250px] items-center justify-center">
                    <AnimatePresence mode="popLayout" initial={false}>
                      <motion.div
                        key={slideKey}
                        className="absolute inset-0 flex items-center justify-center"
                        initial={{
                          opacity: 0,
                          scale: motionDisabled ? 1 : 1.06,
                          y: motionDisabled ? 0 : 16,
                        }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{
                          opacity: 0,
                          scale: motionDisabled ? 1 : 0.96,
                          y: motionDisabled ? 0 : -12,
                        }}
                        transition={{
                          duration: motionDisabled ? 0.4 : 0.55,
                          ease: cinematicEase,
                        }}
                      >
                        <ServiceVisual image={slide.image} />
                      </motion.div>
                    </AnimatePresence>
                  </div>
                </div>
              </div>
            </div>
          </LiquidGlass>

          <div className="mt-4 flex h-8 shrink-0 items-center gap-3">
            <span className="min-w-13 font-mono text-[10px] text-white/50">
              {String(slideIndex + 1).padStart(2, "0")} /{" "}
              {String(category.slides.length).padStart(2, "0")}
            </span>
            <ServiceProgress
              activeCategoryIndex={categoryIndex}
              scrollProgress={scrollYProgress}
              onSelectCategory={jumpToCategory}
            />
            <span className="min-w-14 text-right text-[10px] text-white/55">
              Scroll ↓
            </span>
          </div>
        </div>
      </div>

      <ol className="sr-only">
        {serviceTimeline.map((step) => (
          <li key={`${step.category.title}-${step.slide.title}`}>
            {step.category.title}: {step.slide.title}
          </li>
        ))}
      </ol>
    </section>
  );
}
